import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, FileText, UserCheck, AlertTriangle, Send,
  CheckCircle2, Lock, RotateCcw, UserPlus, Inbox,
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { useNotifications } from "../context/NotificationContext";
import { useLanguage } from "../context/LanguageContext";

const iconByType = {
  case_submitted: Inbox,
  case_claimed: UserCheck,
  needs_correction: AlertTriangle,
  case_resubmitted: Send,
  case_approved: CheckCircle2,
  case_closed: Lock,
  case_force_released: RotateCcw,
  case_reassigned: UserPlus,
};

const iconColorByType = {
  case_submitted: "text-blue-600 bg-blue-50",
  case_claimed: "text-indigo-600 bg-indigo-50",
  needs_correction: "text-rose-600 bg-rose-50",
  case_resubmitted: "text-violet-600 bg-violet-50",
  case_approved: "text-emerald-600 bg-emerald-50",
  case_closed: "text-slate-600 bg-slate-100",
  case_force_released: "text-amber-700 bg-amber-50",
  case_reassigned: "text-indigo-600 bg-indigo-50",
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(iso).toLocaleDateString();
};

const NotificationRow = ({ n, onClick }) => {
  const Icon = iconByType[n.type] || Bell;
  const color = iconColorByType[n.type] || "text-slate-600 bg-slate-100";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 focus-ring transition-colors ${
        n.read ? "" : "bg-blue-50/40"
      }`}
      data-testid={`notif-row-${n.id}`}
    >
      <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${n.read ? "text-slate-600" : "text-[#0A2540] font-medium"}`}>
          {n.message}
        </p>
        <p className="text-xs text-slate-400 mt-0.5">
          {n.case_applicant_name ? `Case · ${n.case_applicant_name} · ` : ""}
          {formatRelative(n.created_at)}
        </p>
      </div>
      {!n.read ? (
        <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
      ) : null}
    </button>
  );
};

export const NotificationBell = () => {
  const { unreadCount, recent, loadRecent, markRead, markAllRead } = useNotifications();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) loadRecent();
  };

  const onRowClick = async (n) => {
    if (!n.read) await markRead(n.id);
    setOpen(false);
    if (n.case_id) navigate(`/cases/${n.case_id}`);
    else if (n.type === "password_reset") navigate("/profile?tab=security");
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative h-9 w-9 rounded-md flex items-center justify-center text-slate-600 hover:text-[#0A2540] hover:bg-slate-50 focus-ring transition-colors"
          data-testid="notification-bell-button"
          aria-label={t("nav_notifications")}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-white"
              data-testid="notification-bell-badge"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="p-0 w-[380px]"
        sideOffset={8}
        data-testid="notification-bell-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <p className="font-display text-sm font-semibold text-[#0A2540]">
            {t("nav_notifications")}
          </p>
          {unreadCount > 0 ? (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-[#007AFF] hover:underline focus-ring rounded px-1"
              data-testid="notif-mark-all-read"
            >
              {t("notif_mark_all_read")}
            </button>
          ) : null}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {recent.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-400 text-sm" data-testid="notif-bell-empty">
              <Bell className="h-6 w-6 mx-auto text-slate-300 mb-2" />
              {t("notif_empty")}
            </div>
          ) : (
            recent.map((n) => (
              <NotificationRow key={n.id} n={n} onClick={() => onRowClick(n)} />
            ))
          )}
        </div>
        <div className="border-t border-slate-200 px-4 py-2 text-center">
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/notifications"); }}
            className="text-xs text-[#007AFF] hover:underline focus-ring rounded px-1"
            data-testid="notif-view-all"
          >
            {t("notif_view_all")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
